import { useEffect, useId, useRef, useState } from 'react';
import '../styles/site-brief-delete.css';

export function SiteBriefDeleteIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5M14 11v5" />
    </svg>
  );
}

export default function SiteBriefDeleteDialog({ name, all = false, onConfirm, onClose }: {
  name?: string;
  all?: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    element?.showModal();
    cancel.current?.focus();
    return () => {
      element?.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus();
      }
    };
  }, []);

  const confirm = async () => {
    if (submitting.current) {
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not delete. Please try again.');
      setBusy(false);
      submitting.current = false;
    }
  };

  return (
    <dialog
      ref={dialog}
      className="brief-delete-dialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-busy={busy}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) {
          onClose();
        }
      }}
    >
      <div className="brief-delete-dialog-symbol"><SiteBriefDeleteIcon /></div>
      <h2 id={titleId}>{all ? 'Delete all your site briefs?' : 'Delete this site brief and session?'}</h2>
      <div id={descriptionId}>
        <p>
          {all
            ? 'This permanently deletes all site briefs you own and all your Vibe Site sessions, including their conversations, requirements, history, and build records.'
            : <>This permanently deletes <strong>{name || 'this site brief'}</strong> and its Vibe Site session, including the conversation, requirements, history, and build records.</>}
        </p>
        <p>Generated Weblium websites are kept. This action cannot be undone.</p>
        {all && <p>Only your own data is deleted, even when the board shows all owners.</p>}
      </div>
      {error && <p className="brief-delete-error" role="alert">{error}</p>}
      <div className="brief-delete-dialog-actions">
        <button ref={cancel} type="button" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="brief-delete-confirm" disabled={busy} onClick={() => { void confirm(); }}>
          {busy ? 'Deleting…' : all ? 'Delete all my briefs & sessions' : 'Delete brief & session'}
        </button>
      </div>
    </dialog>
  );
}
